// БАНК СТРОКИ НАХОДКИ — общий дом для §IV паспорта и блока находки шага 4 (B6-2).
//
// ⭐ ОТВЕТ НА ВОПРОС ТИКЕТА «ОДИН ЭТО УЗЕЛ ИЛИ ДВА» — ОДИН. Строка собирается из двух колонок
// (`s620.d5`, банк `39219875cbb88104ae54f7cd9e867475`): подлежащее по раскладу несущих и предикат
// по геометрии. Обе колонки судились и закрыты один раз, на весь продукт (`s300.d2`, 15/15); две
// копии разошлись бы молча — и разошлись бы не «в разных местах», а в РАЗНЫХ ОТВЕТАХ на один
// вопрос, потому что расходятся они по одному классу за раз.
//
// ⛔ СТРОКИ ЗДЕСЬ НЕ СОЧИНЯЮТСЯ И НЕ ПРАВЯТСЯ. Банк — Notion, дом копи там; это его перенос
// буква-в-букву. Нужна правка предиката — она идёт в Notion-банк, а сюда приезжает следом.
//
// ⚠️ ФУНКЦИЯ ВОЗВРАЩАЕТ ЧАСТИ, А НЕ ГОТОВУЮ СТРОКУ, и это не абстракция ради абстракции:
// у §IV подлежащее и предикат одеты РАЗНЫМИ узлами (`<em>` и `<span class="rd-em">`), а у шага 4
// строка живёт в абзаце блока. Склей их здесь — и один из двух потребителей получил бы разметку
// другого.
//
// ⛔ ПЕЧАТНЫХ ИМЁН СЛОЁВ ЗДЕСЬ НЕТ НАМЕРЕННО, И ЭТО ЗАМЕР, А НЕ ОСТОРОЖНОСТЬ. Они РАЗНЫЕ у двух
// потребителей: §IV печатает `security` (`SectionIV.jsx`), борд шага 4 — `sense of security`
// (`Life Map - s4 Layered Map v10.html:288`). Сведи их сюда — и один из экранов поехал бы.

/** Предикат по классу геометрии, единственное число (банк, часть A). */
export const PREDICATE = {
  'monotonic-descent': 'falls the whole way.',
  'monotonic-ascent': 'rises the whole way.',
  plateau: 'holds steady.',
  'peak-then-fall': 'rises, then falls.',
  'V-shape': 'falls, then rises.',
  'late-cliff': 'holds, then drops.',
  'late-rise': 'holds, then rises.',
  'early-cliff': 'drops, then holds.',
  'early-rise': 'rises, then holds.',
  oscillation: 'rises and falls, over and over.',
  'double-peak': 'rises, dips, rises again.',
  'double-valley': 'falls, lifts, falls again.',
  'way-back': 'falls below the start, then comes back.',
  'high-held-collapse': 'holds high, then gives way.',
  irregular: 'moves, but never into a shape.',
};

/** Подлежащее по раскладу несущих (банк, часть Б). */
export const SUBJECT = {
  singleton: 'the red line',
  pair: 'the red line',
  triplet: 'the three lines',
  field: 'every line',
  split: 'two lines, pulling apart',
  scatter: 'no single line carries it',
};

/** Раскладки, у которых подлежащее во множественном числе (R10 fix). */
export const PLURAL_SUBJECT = new Set(['triplet', 'split']);

/** Тот же предикат во множественном числе — согласование по числу подлежащего. */
export const PREDICATE_PL = {
  'monotonic-descent': 'fall the whole way.',
  'monotonic-ascent': 'rise the whole way.',
  plateau: 'hold steady.',
  'peak-then-fall': 'rise, then fall.',
  'V-shape': 'fall, then rise.',
  'late-cliff': 'hold, then drop.',
  'late-rise': 'hold, then rise.',
  'early-cliff': 'drop, then hold.',
  'early-rise': 'rise, then hold.',
  oscillation: 'rise and fall, over and over.',
  'double-peak': 'rise, dip, rise again.',
  'double-valley': 'fall, lift, fall again.',
  'way-back': 'fall below the start, then come back.',
  'high-held-collapse': 'hold high, then give way.',
  irregular: 'move, but never into a shape.',
};

/** Запасной предикат — он же был запасным у §IV до выноса банка. */
export const PREDICATE_FALLBACK = 'the red line.';

// F8 (`s505.d1`, ратифицировано Кириллом 18.08): на несущей ровно из трёх отвеченных точек ярлык
// `irregular` — утверждение о данных, которых у нас нет. Две дельты не «не сложились в форму»
// потому, что жизнь нерегулярна: они не сложились потому, что мы мало спросили, и печатать
// «формы нет» значит переложить наш пробел выборки на читающего. Поэтому форма не называется:
// предикат уходит в нейтральный, в обоих числах.
export const NEUTRAL_PRED_N3 = 'runs from first to last.';
export const NEUTRAL_PRED_N3_PL = 'run from first to last.';

/** Тот самый случай F8: `irregular` ровно на трёх отвеченных точках несущей. */
export function shapeless(view) {
  return view.geometryClass === 'irregular'
    && Array.isArray(view.carrier)
    && view.carrier.length === 3;
}

/** Сколько этапов несущей отвечено (не `null`). Не массив — ноль. */
export function answeredPoints(carrier) {
  return Array.isArray(carrier) ? carrier.filter((x) => x != null).length : 0;
}

// Н41 (`s950.d1` / `s950.d3`, Кирилл 30.09): по одной отвеченной точке линию не строим и форму не
// называем. Одна точка бывает только у singleton (current / historical, замер Н41 п. 4); ноль
// недостижим (п. 3). Движок при этом честно отдаёт `plateau` (спека :208, best-effort N=1), поэтому
// гейт стоит здесь, у читателей, а не в классификаторе: один признак на заголовок (тут), курсив
// (`chartCopy.js`), THE SHAPE и чарт (§IV, шаг 5). Не путать с `shapeless` (F8): там точек три.
/** Несущая с меньше чем двумя отвеченными точками — форма не называется нигде. */
export function onePoint(view) {
  return Array.isArray(view.carrier) && answeredPoints(view.carrier) < 2;
}

/**
 * Части строки находки.
 *
 * ⭐ `scatter` — ПОДЛЕЖАЩЕЕ БЕЗ ПРЕДИКАТА, и это не пропуск: сказать «нет несущей» и следом
 * назвать её форму нельзя (`s617.d1`). Возвращается `predicate: null` — потребитель обязан
 * увидеть отсутствие, а не получить пустую строку и не заметить разницы.
 * ⚠️ `predicate: null` бывает и у несущей с одной точкой (Н41) — признаком «нет несущей» он НЕ
 * служит, разброс узнаётся по `carrierConfig`.
 *
 * @param {{carrierConfig: string, geometryClass: string, carrier?: Array<number|null>}} view
 * @returns {{subject: string, predicate: string|null, plural: boolean, separator: string}}
 */
export function findingParts(view) {
  const subject = SUBJECT[view.carrierConfig];
  if (view.carrierConfig === 'scatter') {
    return { subject, predicate: null, plural: false, separator: ' ' };
  }
  const plural = PLURAL_SUBJECT.has(view.carrierConfig);
  // Н41: одна точка — подлежащее без предиката, по прецеденту `scatter` («the red line.»).
  if (onePoint(view)) return { subject, predicate: null, plural, separator: ' ' };
  const predicate = shapeless(view)
    ? (plural ? NEUTRAL_PRED_N3_PL : NEUTRAL_PRED_N3)
    : ((plural ? PREDICATE_PL : PREDICATE)[view.geometryClass] || PREDICATE_FALLBACK);
  // У `split` подлежащее само кончается запятой-вставкой («two lines, pulling apart»), поэтому
  // шов между колонками у него запятой, а не пробелом. Байт §IV, не новое правило.
  return { subject, predicate, plural, separator: view.carrierConfig === 'split' ? ', ' : ' ' };
}
